import { useSelector } from "react-redux"
import { State } from "../reducers"
import { CatalogoProducto, Impuesto, ProductoImpuesto } from "../interfaces/utils/utils.interface"

export const formatearNum = (numero: any) => {
  if (numero === "" || numero === null || numero === undefined) return "$0.00";
  return numero.toLocaleString('es-SV', {minimumFractionDigits: 2, maximumFractionDigits: 2, style: 'currency', currency: 'USD'}) ?? '--'
}

export default function useUtilsDocs() {
  const { productos } = useSelector((state: State) => state.catalogos);

  return {
    esCatalogoProductoByTipo: 
      (esFormServicio: boolean, productoCatalogo: CatalogoProducto) => esCatalogoProductoByTipo(esFormServicio, productoCatalogo),
    processFormCatalogoProducto: 
      (tipoDte: string, form: any, codeProducto: string) => processFormCatalogoProducto(productos, tipoDte, form, codeProducto),
    processOtrosImpuestosCalculo: 
      (tipoDte: string, form: any, subtotal: number, esProductoForm: boolean = true) => processOtrosImpuestosCalculo(productos, tipoDte, form, subtotal, esProductoForm),
    procesOtrosImpuestosResumen: 
      (tipoDte: string, resumenActual: Array<any>, form: any, subtotal: number) => procesOtrosImpuestosResumen(productos, tipoDte, resumenActual, form, subtotal),
    getTributosCatalogoProducto:
      (codeProducto: string) => getTributosCatalogoProducto(productos, codeProducto),
  };
}

export const esCatalogoProductoByTipo = (esFormServicio: boolean, productoCatalogo: CatalogoProducto): boolean => {
  return (
    esFormServicio && productoCatalogo.tipoitem === "0" // Servicio
  ) || (
    !esFormServicio && productoCatalogo.tipoitem === "1" // Producto
  );
};

export const getTributosCatalogoProducto = (productos: CatalogoProducto[], codeProducto: string):Array<string> => {
  if (!codeProducto || productos.length === 0) return [];
  
  const productSelected: CatalogoProducto|undefined = productos.find((producto: CatalogoProducto) => producto.clave === codeProducto);
  if (!productSelected) return [];

  const tributos = productSelected.ProductoImpuestos
    .map(({ Impuesto }: ProductoImpuesto) => (Impuesto.encuerpodoc ? Impuesto.codigo : ''))
    .filter(codigo => codigo !== '');
  
  return tributos;
}

export const processFormCatalogoProducto = (productos: CatalogoProducto[], tipoDte: string, form: any, codeProducto: string) => {
  let newFormProducto;
  const productSelected: CatalogoProducto|undefined = productos.find((producto: CatalogoProducto) => producto.clave === codeProducto);
  if (!productSelected) return form;

  const tributos = productSelected.ProductoImpuestos
    .map(({ Impuesto }: ProductoImpuesto) => (Impuesto.encuerpodoc ? Impuesto.codigo : null))
    .filter(codigo => codigo !== null);

  switch (tipoDte) {
    case "01":
    case "14":
      newFormProducto = {
        ...form,
        clave: productSelected.clave,
        tipoDescuento: productSelected.descuentoporcentaje ? "porcentaje" : "monto",
        descuento: productSelected.descuento ?? productSelected.descuentodefault,
        descripcion: productSelected.descripcion,
        tipo: productSelected.tipoitem === "0" ? "s" : "p",
        precio: productSelected.preciounitario ?? productSelected.preciounitariodefault,
        unidadMedida: productSelected.unidad,
        tributos: tributos,
      }
      break;

    case "03":
    case "04":
    case "05":
    case "06":
    case "11":
      newFormProducto = {
        ...form,
        clave: productSelected.clave,
        conIva: productSelected.ivaincluido,
        descripcion: productSelected.descripcion,
        descuento: productSelected.descuento ?? productSelected.descuentodefault,
        exento: productSelected.exento,
        servicio: productSelected.tipoitem === "0",
        tipoDescuento: productSelected.descuentoporcentaje ? "Porcentaje" : "Monto",
        precio: productSelected.preciounitario ?? productSelected.preciounitariodefault,
        unidadMedida: productSelected.unidad,
        tributos: tributos,
      }
      break;

    default:
      newFormProducto = { ...form };
      break;
  }

  return newFormProducto;
};

// Función auxiliar para calcular los tributos
export const calcularTributo = (tipoDte: string, form: any, subtotal: number, tributo: Impuesto) => {
  let totalTributo = 0;
  const valorImpuesto = parseFloat(`${tributo.impuesto}`);

  switch (tipoDte) {
    case "01":
    case "03":
    case "04":
    case "05":
    case "06":
      if (parseFloat(form.cantidad || 0) >= tributo.cantidadaplica) {
        if (tributo.esporcentaje) totalTributo += subtotal * (valorImpuesto / 100);
        else {
          const aplica = form.cantidad / tributo.cantidadaplica;
          for (let i = 0; i < aplica; i++) {
            totalTributo += valorImpuesto;
          }
        }
      }
      break;

    default:
      break;
  }

  return totalTributo;
};

// Función auxiliar para procesar otros impuestos cálculo
export const processOtrosImpuestosCalculo = (productos: CatalogoProducto[], tipoDte: string, form: any, subtotal: number, esProductoForm: boolean = true): number => {
  try {
    let totalTributos = 0;
    const productSelected: CatalogoProducto|undefined = productos.find((producto: CatalogoProducto) => producto.clave === form?.clave);

    if (!productSelected) return 0;
    for (const itemImpuesto of productSelected.ProductoImpuestos) {
      const tributo = itemImpuesto.Impuesto;

      // esProductoForm -> Si es el calculo en un formulario de producto o en el resumen de productos general
      const calculaTributo = esProductoForm ? tributo.enresumensitioprod : tributo.enresumensitiodoc;
      if (!calculaTributo) continue;

      totalTributos += calcularTributo(tipoDte, form, subtotal, tributo);
    }

    return totalTributos;
  } catch (e) {
    return 0;
  }
};

// Función auxiliar para procesar otros impuestos resumen
export const procesOtrosImpuestosResumen = (productos: CatalogoProducto[], tipoDte: string, resumenActual: Array<any>, form: any, subtotal: number): Array<string> => {
  try {
    const productSelected: CatalogoProducto|undefined = productos.find((producto: CatalogoProducto) => producto.clave === form?.clave);
    if (!productSelected) return resumenActual;

    for (const itemImpuesto of productSelected.ProductoImpuestos) {
      let totalTributo = 0;
      const tributo = itemImpuesto.Impuesto;

      if (!tributo.enresumen) continue;
      totalTributo += calcularTributo(tipoDte, form, subtotal, tributo);

      const tributoResumen = resumenActual.find((resum) => resum.codigo === tributo.codigo);
      if (!tributoResumen) {
        resumenActual.push({ codigo: tributo.codigo, valor: totalTributo, descripcion: tributo.descripcion });
      } else {
        tributoResumen.valor += totalTributo;
      }
    }

    return resumenActual;
  } catch (e) {
    return resumenActual;
  }
};